import { PolicyBadge } from '../../../policy/policy_badge.js';
import { CheckCircleIcon, ClockIcon, MinusCircleIcon, XCircleIcon } from '@phosphor-icons/react';
import {
  SidePanel,
  SidePanelBody,
  SidePanelContent,
  SidePanelHeader,
  SidePanelTitle,
} from '@voll-intelligence/ui-kit/ui/side-panel';
import { fmtDate, fmtMoney, fmtTime } from '../flight_format.js';
import type { FlightOfferSource, FlightPolicyRuleSource } from '../flight_offer_source.js';
import './flight_policy_drawer.css';

/** Os textos da gaveta de política, por prop (#4997). O genui-kit monta em
 *  `flightPolicyCopy(locale)`. */
export type FlightPolicyCopy = {
  readonly title: string;
  readonly close: string;
  readonly inPolicy: string;
  readonly outPolicy: string;
  /** O título de uma regra pela chave do produtor; `undefined` (produtor mais
   *  novo) cai no `title` do fio, que é o fallback pt-BR. */
  readonly ruleTitle: (key: string) => string | undefined;
  /** A janela de antecedência ("Reserve com 7 dias de antecedência…"). */
  readonly bookingWindow: (days: number, scope: 'domestic' | 'international') => string;
};

/**
 * Drawer "Detalhes da Política" — replica a tela do app. Abre a partir do selo
 * de política do card (dentro OU fora) e mostra o resumo da oferta + uma seção
 * por regra (antecedência/classe/bagagem) com status próprio e o texto rico
 * (`rule.text`, de flightPolicyDescriptives — fail-soft: ausente → só título +
 * status). A casca é o `SidePanel` do DS (`wide`), que também o põe acima do drawer
 * de filtro; conteúdo em `.pdrawer-*` (flight_policy_drawer.css). Display-only —
 * nenhuma autorização passa por aqui. Montado = aberto: quem abre decide quando.
 */
export function FlightPolicyDrawer({
  offer,
  onClose,
  copy,
  locale,
}: {
  readonly offer: FlightOfferSource;
  readonly onClose: () => void;
  readonly copy: FlightPolicyCopy;
  /** O idioma das datas. */
  readonly locale: string;
}) {
  const policy = offer.policy;
  const seg = offer.segments[0];
  const out = policy?.status === 'out';

  return (
    <SidePanel
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SidePanelContent size="wide" closeLabel={copy.close}>
        <SidePanelHeader>
          <SidePanelTitle>{copy.title}</SidePanelTitle>
        </SidePanelHeader>
        <SidePanelBody className="pdrawer-body">
          {/* O MESMO selo do recibo de hotel/voo (`PolicyBadge` do DS), não uma
              pílula própria: o veredito é a afirmação de política desta tela, e a
              versão local vinha em mono/uppercase e ÂMBAR — âmbar é urgência, e o
              furo de política é `danger` em todo o resto do produto. */}
          <PolicyBadge
            tone={out ? 'outside' : 'within'}
            label={out ? copy.outPolicy : copy.inPolicy}
          />

          {seg && (
            <div className="pdrawer-flight">
              <div className="pf-route">
                {seg.from} <span className="pf-sep">→</span> {seg.to}
              </div>
              <div className="pf-line">
                <span className="pf-time">{fmtTime(seg.departAt)}</span>
                <span className="pf-mid">
                  {seg.durationLabel && (
                    <span className="pf-dur">
                      <ClockIcon size={11} aria-hidden />
                      {seg.durationLabel}
                    </span>
                  )}
                </span>
                <span className="pf-time">{fmtTime(seg.arriveAt)}</span>
              </div>
              <div className="pf-foot">
                <span className="pf-sub">{fmtDate(seg.departAt, locale)}</span>
                <span className="pf-price">{fmtMoney(offer.price)}</span>
              </div>
            </div>
          )}

          <ul className="pdrawer-rules">
            {(policy?.rules ?? []).map((r) => (
              <PolicyRuleRow key={r.key} rule={r} copy={copy} />
            ))}
          </ul>
        </SidePanelBody>
      </SidePanelContent>
    </SidePanel>
  );
}

function PolicyRuleRow({
  rule,
  copy,
}: {
  readonly rule: FlightPolicyRuleSource;
  readonly copy: FlightPolicyCopy;
}) {
  const title = copy.ruleTitle(rule.key) ?? rule.title;
  const text = rule.params ? copy.bookingWindow(rule.params.days, rule.params.scope) : rule.text;
  let icon = <MinusCircleIcon size={18} aria-hidden />;
  if (rule.status === 'ok') {
    icon = <CheckCircleIcon size={18} aria-hidden />;
  } else if (rule.status === 'violated') {
    icon = <XCircleIcon size={18} aria-hidden />;
  }
  return (
    <li className={`pdrawer-rule ${rule.status}`}>
      <span className="pr-icon" aria-hidden="true">
        {icon}
      </span>
      <div className="pr-body">
        <span className="pr-title">{title}</span>
        {text && <p className="pr-text">{text}</p>}
      </div>
    </li>
  );
}
